"use client";

import { useState } from "react";
import { Icon } from "./Icons";
import {
  ConfirmDialog,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusPill,
  formatMoney
} from "./UI";

const EMPTY_ROOM = {
  name: "",
  code: "",
  type: "Double",
  capacity: 2,
  targetNet: 466.75,
  status: "active",
  color: "#6558d3",
  notes: ""
};

export default function RoomsPage({ data, actions }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ROOM);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function openCreate() {
    setForm(EMPTY_ROOM);
    setEditing({ id: null });
    setError("");
  }

  function openEdit(room) {
    setForm({ ...EMPTY_ROOM, ...room });
    setEditing(room);
    setError("");
  }

  function update(key) {
    return (event) => {
      const value =
        event.target.type === "number"
          ? Number(event.target.value)
          : event.target.value;
      setForm((current) => ({ ...current, [key]: value }));
    };
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (editing.id) {
        await actions.update("rooms", editing.id, form, "Room updated");
      } else {
        await actions.create("rooms", form, "Room added");
      }
      setEditing(null);
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    try {
      const hasReservations = data.reservations.some(
        (reservation) => reservation.roomId === removeTarget.id
      );
      if (hasReservations) {
        await actions.update(
          "rooms",
          removeTarget.id,
          { status: "inactive" },
          "Room archived because it has reservation history"
        );
      } else {
        await actions.remove("rooms", removeTarget.id, "Room deleted");
      }
      setRemoveTarget(null);
    } catch (removeError) {
      actions.notify(removeError.message, "error");
    }
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Inventory"
        title="Rooms"
        text="Define every sellable room, its capacity, status and default client total."
        action={
          <button className="button button-primary" onClick={openCreate}>
            <Icon name="plus" size={18} />
            Add room
          </button>
        }
      />

      <div className="stats-strip">
        <div>
          <span>Total rooms</span>
          <strong>{data.rooms.length}</strong>
        </div>
        <div>
          <span>Sellable now</span>
          <strong>
            {data.rooms.filter((room) => room.status === "active").length}
          </strong>
        </div>
        <div>
          <span>Average client total</span>
          <strong>
            {formatMoney(
              data.rooms.reduce(
                (sum, room) => sum + Number(room.targetNet || 0),
                0
              ) / Math.max(1, data.rooms.length),
              data.settings.currency
            )}
          </strong>
        </div>
      </div>

      {data.rooms.length ? (
        <div className="room-grid">
          {data.rooms.map((room) => {
            const futureBookings = data.reservations.filter(
              (reservation) =>
                reservation.roomId === room.id &&
                reservation.checkOut >= new Date().toISOString().slice(0, 10) &&
                reservation.status !== "cancelled"
            ).length;
            return (
              <article className="room-card card" key={room.id}>
                <div
                  className="room-card-accent"
                  style={{ backgroundColor: room.color }}
                />
                <div className="room-card-top">
                  <span
                    className="room-icon"
                    style={{
                      color: room.color,
                      backgroundColor: `${room.color}18`
                    }}
                  >
                    <Icon name="bed" size={24} />
                  </span>
                  <div className="card-actions">
                    <button
                      className="icon-button"
                      onClick={() => openEdit(room)}
                      aria-label={`Edit ${room.name}`}
                    >
                      <Icon name="edit" size={18} />
                    </button>
                    <button
                      className="icon-button icon-button-danger"
                      onClick={() => setRemoveTarget(room)}
                      aria-label={`Delete ${room.name}`}
                    >
                      <Icon name="trash" size={18} />
                    </button>
                  </div>
                </div>
                <span className="room-code">Room {room.code}</span>
                <h2>{room.name}</h2>
                <p>{room.notes || `${room.type} room`}</p>
                <div className="room-card-meta">
                  <span>
                    <Icon name="user" size={16} />
                    Up to {room.capacity}
                  </span>
                  <span>
                    <Icon name="calendar" size={16} />
                    {futureBookings} upcoming
                  </span>
                </div>
                <div className="room-card-bottom">
                  <span>
                    <small>Default client total / night</small>
                    <strong>
                      {formatMoney(room.targetNet, data.settings.currency)}
                    </strong>
                  </span>
                  <StatusPill value={room.status} />
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon="rooms"
          title="No rooms yet"
          text="Add your first room to start calculating rates and accepting reservations."
          action={
            <button className="button button-primary" onClick={openCreate}>
              Add first room
            </button>
          }
        />
      )}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        eyebrow="Room definition"
        title={editing?.id ? "Edit room" : "Add a room"}
      >
        <form onSubmit={save}>
          <div className="form-grid">
            <Field label="Room name">
              <input
                value={form.name}
                onChange={update("name")}
                placeholder="Felix Double"
                required
              />
            </Field>
            <Field label="Room number / code">
              <input
                value={form.code}
                onChange={update("code")}
                placeholder="101"
                required
              />
            </Field>
            <Field label="Room type">
              <select value={form.type} onChange={update("type")}>
                <option>Single</option>
                <option>Double</option>
                <option>Twin</option>
                <option>Triple</option>
                <option>Suite</option>
                <option>Apartment</option>
                <option>Family</option>
              </select>
            </Field>
            <Field label="Maximum guests">
              <input
                type="number"
                min="1"
                max="20"
                value={form.capacity}
                onChange={update("capacity")}
                required
              />
            </Field>
            <Field label={`Default client total (${data.settings.currency})`}>
              <input
                type="number"
                min="0"
                step="10"
                value={form.targetNet}
                onChange={update("targetNet")}
                required
              />
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={update("status")}>
                <option value="active">Active</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Room colour">
              <div className="color-field">
                <input
                  type="color"
                  value={form.color}
                  onChange={update("color")}
                />
                <span>{form.color}</span>
              </div>
            </Field>
            <Field className="field-span-2" label="Notes">
              <textarea
                rows="3"
                value={form.notes}
                onChange={update("notes")}
                placeholder="Bed setup, balcony, amenities…"
              />
            </Field>
          </div>
          {error ? (
            <div className="inline-alert inline-alert-error">
              <Icon name="warning" />
              {error}
            </div>
          ) : null}
          <div className="modal-actions">
            <button
              className="button button-secondary"
              onClick={() => setEditing(null)}
              type="button"
            >
              Cancel
            </button>
            <button className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : editing?.id ? "Save changes" : "Add room"}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(removeTarget)}
        title="Remove this room?"
        text={
          removeTarget
            ? `${removeTarget.name} will be deleted. If it has reservation history, it will be archived instead.`
            : ""
        }
        onClose={() => setRemoveTarget(null)}
        onConfirm={remove}
      />
    </div>
  );
}
